import React from "react";

import { Box, Card, Divider, Typography, useTheme } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { makeStyles, withStyles } from "tss-react/mui";

import CookingMethodForm from "./CookingMethodForm";
import IngredientsForm from "./IngredientsForm";
import Macronutrients from "./Macronutrients";
import RecipeDetailsForm from "./RecipeDetailsForm";
import { RecipeFormValues } from "./RecipeForm";
import RecipeFormFooter from "./RecipeFormFooter";

interface RecipeFormProps {
  title?: string;
  submitLabel?: string;
  onCancel?: () => void;
}

const Content = withStyles(Card, () => ({
  root: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    marginTop: "16px",
    marginBottom: "64px",
  },
}));

const useStyles = makeStyles()((theme) => ({
  divider: {
    width: "3px",
  },
  bodyText1: {
    fontSize: 20,
    fontWeight: 700,
  },
  bodyText2: {
    fontSize: 16,
    fontWeight: 600,
  },
  bodyText4: {
    fontSize: 14,
    fontWeight: 600,
  },
}));

const RecipeFormContent: React.FC<RecipeFormProps> = ({
  title = "Add Recipe",
  submitLabel = "Create Recipe",
  onCancel = () => {},
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { watch } = useFormContext<RecipeFormValues>();

  const renderActiveStepContent = () => {
    switch (watch("meta.activeStep")) {
      case 0:
        return (
          <Box display="flex">
            <Box padding={theme.spacing(3)}>
              <Typography className={classes.bodyText1}>
                Recipe details
              </Typography>
              <Box margin={theme.spacing(1)} />
              <RecipeDetailsForm />
            </Box>
            <Divider
              flexItem
              orientation="vertical"
              className={classes.divider}
            />
            <Box flex={1} padding={theme.spacing(3)}>
              <Typography className={classes.bodyText1} variant="body1">
                Macronutrients
              </Typography>
              <Box margin={2} />
              <Macronutrients />
              <Box margin={6} />
              <Typography className={classes.bodyText1}>Ingredients</Typography>
              <IngredientsForm />
            </Box>
          </Box>
        );
      case 1:
        return <CookingMethodForm />;
      default:
        return null;
    }
  };

  return (
    <Content>
      <Box padding={theme.spacing(4, 3, 8, 3)}>
        <Typography variant="h5" fontWeight={800}>
          {title}
        </Typography>
      </Box>
      <Box margin={theme.spacing(0.5)} />
      <Divider />
      <Box flex={1}>{renderActiveStepContent()}</Box>
      <RecipeFormFooter submitLabel={submitLabel} onCancel={onCancel} />
    </Content>
  );
};

export default RecipeFormContent;
